import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Icon,
  NumberInput,
  NumberInputField,
  Text,
} from '@chakra-ui/react';
import { KeyTermType, KeyTermUnit } from '@client/graphql/__generated__/types';
import isNil from 'lodash/isNil';
import { CircleAlertIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CurrencyInput } from '~/common/components/Inputs/CurrencyInput';
import { SignalStrength } from '~/common/icons/SignalIcon';
import { formatToCurrency } from '~/services/document/utils/number';
import { formatAgentRecommendation } from '../common.utils';
import {
  MOBILE_BASE_VIEW_ONLY_HEIGHT,
  MOBILE_COLLAPSED_PP_ROW_HEIGHT,
  MOBILE_COLLAPSED_ROW_HEIGHT,
  MOBILE_EXPANDED_PP_ROW_HEIGHT,
  MOBILE_EXPANDED_ROW_HEIGHT,
} from '../constants';
import { useKeyTermOverrideLogic } from '../hooks/useKeyTermOverrideLogic';
import { KeyTermsRangeChart } from '../KeyTermRangeChart/KeyTermsRangeChart';
import { KeyTermsRowProps } from '../KeyTermsRow';
import { NotesPopover } from '../NotesPopover';
import { SourceTags } from '../SourceTags';

export const KeyTermsMobileCard = <T extends KeyTermType>(
  props: KeyTermsRowProps<T>
) => {
  const {
    isExpanded,
    setExpandedRow,
    label,
    range,
    row,
    unit,
    source,
    confidence,
    distanceMiles,
    timePeriodMonths,
    errorMessage,
    value,
    notes,
    onUpdateValue,
    onUpdateNotes,
    buyersAgentUser,
    isViewOnly,
  } = props;

  const [inputValue, setInputValue] = useState<number | null | undefined>(
    value
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    isSingleRangeValue,
    getRangeTrackTooltipContent,
    getMainValueLabel,
    getAnchor,
    getDotColorValue,
    getHighlightedRange,
    getPlaceholderValue,
    getRangeLabel,
  } = useKeyTermOverrideLogic({
    ...props,
    row,
  });

  const isNilValue = isNil(value);

  const sourceLabel = source?.toLowerCase()?.split('_')?.join(' ');

  const showRangeChart = !!(!isSingleRangeValue && range && unit);

  const placeholder = getPlaceholderValue();

  const rangeLabel = getRangeLabel();

  const mainValueLabel = getMainValueLabel?.();

  const getCardHeight = () => {
    if (isExpanded) {
      return row === KeyTermType.PURCHASE_PRICE
        ? MOBILE_EXPANDED_PP_ROW_HEIGHT
        : MOBILE_EXPANDED_ROW_HEIGHT;
    }

    if (isViewOnly && (!showRangeChart || isNilValue)) {
      return MOBILE_BASE_VIEW_ONLY_HEIGHT;
    }

    return row === KeyTermType.PURCHASE_PRICE
      ? MOBILE_COLLAPSED_PP_ROW_HEIGHT
      : MOBILE_COLLAPSED_ROW_HEIGHT;
  };

  const dotColor = getDotColorValue(inputValue);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <Card pt={3}>
      <CardHeader px={3} py={0}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontWeight="medium">{label}</Text>
          <Flex minWidth={6}>
            <NotesPopover
              isViewOnly={isViewOnly}
              label={label}
              noteUser={buyersAgentUser}
              notes={notes}
              onChange={(notes) => onUpdateNotes(notes)}
            />
          </Flex>
        </Flex>
      </CardHeader>
      <CardBody p={1} py={0}>
        <Flex
          ref={containerRef}
          direction="column"
          gap={2}
          justifyContent="center"
          px={2}
          onBlur={() => {
            if (!isViewOnly) {
              setExpandedRow(null);
            }
          }}
          onClick={() => {
            if (inputRef.current !== document.activeElement) {
              inputRef.current?.focus();
            }
          }}
          onFocus={() => {
            if (!isSingleRangeValue && range && !isViewOnly) {
              setExpandedRow(row);
            }
          }}
          onMouseDown={() => {
            if (
              inputRef.current !== document.activeElement &&
              !isExpanded &&
              !isViewOnly
            ) {
              inputRef.current?.focus();
            }
          }}
        >
          <Flex
            alignItems="center"
            gap={2}
            height={getCardHeight()}
            transition="height 0.3s ease-in-out"
          >
            <Flex
              direction="column"
              height="100%"
              justifyContent={
                isViewOnly
                  ? showRangeChart && !isNilValue
                    ? 'flex-end'
                    : 'flex-start'
                  : 'space-between'
              }
              py={4}
              width="100%"
            >
              {!isViewOnly && (
                <Flex gap={4} height="100%">
                  <Flex flexBasis={0} flexGrow={1} height="100%">
                    <Flex direction="column" gap={1}>
                      <Text
                        color="whiteAlpha.600"
                        fontSize="sm"
                        fontWeight="medium"
                        textTransform="uppercase"
                      >
                        Market Data
                      </Text>
                      {mainValueLabel}

                      {!!range && (
                        <>
                          <Text
                            color={mainValueLabel ? 'whiteAlpha.600' : 'white'}
                            fontSize={mainValueLabel ? '16px' : '20px'}
                            fontWeight={mainValueLabel ? 'normal' : 'medium'}
                            lineHeight="1"
                          >
                            {rangeLabel}
                          </Text>
                          <Flex alignItems="center" gap={2}>
                            <SourceTags
                              confidence={
                                confidence as unknown as SignalStrength
                              }
                              distanceMiles={distanceMiles}
                              isExpanded={isExpanded}
                              sourceLabel={sourceLabel}
                              timePeriodMonths={timePeriodMonths}
                            />
                          </Flex>
                        </>
                      )}
                      {errorMessage && (
                        <Flex alignItems="center" gap={2}>
                          <Icon as={CircleAlertIcon} color="red.500" />
                          <Text>{errorMessage}</Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                  <Flex direction="column" flexBasis={0} flexGrow={1} gap={2}>
                    <Text
                      color="whiteAlpha.600"
                      fontSize="sm"
                      fontWeight="medium"
                      textTransform="uppercase"
                    >
                      Your Recommendation
                    </Text>
                    <Flex gap={2} justifyContent="center">
                      {isViewOnly ? (
                        range && unit ? (
                          <Flex alignItems="flex-end" mt={6} width="100%">
                            <KeyTermsRangeChart
                              anchor={getAnchor?.()}
                              dotColor={dotColor}
                              fullRange={range}
                              getRangeTrackTooltipContent={
                                getRangeTrackTooltipContent
                              }
                              highlightedRange={getHighlightedRange?.()}
                              showValue={true}
                              source={source}
                              unit={unit}
                              value={value}
                            />
                          </Flex>
                        ) : (
                          <Text>{value}</Text>
                        )
                      ) : (
                        <Flex
                          justifyContent="center"
                          position="relative"
                          width="100%"
                        >
                          {unit === KeyTermUnit.USD && (
                            <CurrencyInput
                              ref={inputRef}
                              data-testid={`${row}-input`}
                              fontSize="20px"
                              fontWeight="medium"
                              placeholder={
                                placeholder
                                  ? formatToCurrency(placeholder)
                                  : '$0'
                              }
                              size="sm"
                              value={inputValue ?? undefined}
                              onBlur={() => {
                                onUpdateValue(inputValue);
                              }}
                              onValueChange={(value) => {
                                const newParsedValue = value
                                  ? Number.parseFloat(value)
                                  : null;
                                setInputValue(newParsedValue);
                              }}
                            />
                          )}
                          {unit === KeyTermUnit.DAYS && (
                            <NumberInput
                              size="md"
                              value={inputValue ?? ''}
                              width="100%"
                              onBlur={() => {
                                onUpdateValue(inputValue);
                              }}
                              onChange={(_, value) => {
                                if (isNaN(value)) {
                                  setInputValue(null);
                                } else {
                                  setInputValue(value);
                                }
                              }}
                            >
                              <NumberInputField
                                ref={inputRef}
                                backgroundColor="input.bg"
                                data-testid={`${row}-input`}
                                fontSize="20px"
                                fontWeight="medium"
                                placeholder={
                                  placeholder ? `${placeholder} days` : '0 days'
                                }
                                value={value ?? ''}
                              />
                            </NumberInput>
                          )}
                          {showRangeChart && !isNilValue && (
                            <Flex
                              backgroundColor={dotColor}
                              borderRadius="full"
                              boxSize={2}
                              position="absolute"
                              right={2}
                              top={{ base: 4, md: 5 }}
                            />
                          )}
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              )}

              {((isExpanded && showRangeChart) ||
                (isViewOnly && showRangeChart && !isNilValue)) && (
                <KeyTermsRangeChart
                  anchor={getAnchor?.()}
                  dotColor={dotColor}
                  fullRange={range}
                  getRangeTrackTooltipContent={getRangeTrackTooltipContent}
                  highlightedRange={getHighlightedRange?.()}
                  showValue={isViewOnly}
                  source={source}
                  unit={unit}
                  value={inputValue}
                />
              )}
              {isViewOnly && (!showRangeChart || isNilValue) && (
                <Text
                  color={isNilValue ? 'whiteAlpha.600' : 'white'}
                  fontSize="24px"
                  fontWeight="medium"
                >
                  {!isNilValue
                    ? formatAgentRecommendation({
                        value,
                        unit,
                        shortForm: false,
                      })
                    : 'To be discussed'}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </CardBody>
      {isViewOnly && !errorMessage && (
        <CardFooter>
          <Flex justifyContent="space-between" width="100%">
            <Flex direction="column" gap={1}>
              <Text
                color="whiteAlpha.600"
                fontSize="sm"
                fontWeight="medium"
                textTransform="uppercase"
              >
                Market Data
              </Text>
              <Flex alignItems="center" gap={2}>
                {mainValueLabel}
              </Flex>
              {!!range && (
                <Text
                  color={mainValueLabel ? 'whiteAlpha.600' : 'white'}
                  fontSize={mainValueLabel ? '16px' : '20px'}
                  fontWeight={mainValueLabel ? 'normal' : 'medium'}
                  lineHeight="1"
                >
                  {rangeLabel}
                </Text>
              )}
            </Flex>
            <SourceTags
              confidence={confidence as unknown as SignalStrength}
              distanceMiles={distanceMiles}
              isExpanded={isExpanded}
              sourceLabel={sourceLabel}
              timePeriodMonths={timePeriodMonths}
            />
          </Flex>
        </CardFooter>
      )}
    </Card>
  );
};
